// admin/pages/Ideas.tsx — paginated Story Ideas table backed by the admin socket.
//
// Mirrors pages/Users.tsx structure: filter input (debounced 300ms), status
// + sort dropdowns, page-size selector, sortable columns, sortable-column
// arrows, stale-ack guard via requestId echo, paging controls. Row hover
// reveals an expand toggle; expanded view shows full body + read-only
// admin notes placeholder.
//
// v1 is view-only: status pills render but don't mutate, the Save button on
// the expanded notes textarea is disabled. The schema hook is there; the
// admin update RPC is out of scope for this iteration.

import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import type { Socket } from "socket.io-client";
import { styles, tokens } from "../styles";
import {
  ideasActions,
  useAppDispatch,
  useAppSelector,
} from "../store";
import { RpcError } from "../socket/rpc";
import type {
  AdminStoryIdea,
  AdminStoryIdeaStatus,
  AdminStoryIdeasSort,
} from "../store/types";

interface IdeasPageProps {
  socket: RefObject<Socket | null>;
}

const PAGE_SIZES = [25, 50, 100] as const;

const STATUS_OPTIONS: Array<{
  value: "all" | AdminStoryIdeaStatus;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "triaged", label: "Triaged" },
  { value: "shipped", label: "Shipped" },
  { value: "dismissed", label: "Dismissed" },
];

const SORT_OPTIONS: Array<{
  value: string;
  label: string;
  sort: AdminStoryIdeasSort;
  order: "asc" | "desc";
}> = [
  { value: "created_at:desc", label: "Newest first", sort: "created_at", order: "desc" },
  { value: "created_at:asc",  label: "Oldest first", sort: "created_at", order: "asc" },
  { value: "user_email:asc",  label: "User A→Z",     sort: "user_email", order: "asc" },
  { value: "user_email:desc", label: "User Z→A",     sort: "user_email", order: "desc" },
];

interface Column {
  key: AdminStoryIdeasSort | "preview" | "status";
  label: string;
  sortable: boolean;
  width?: number;
  align?: "left" | "right";
}

const COLUMNS: Column[] = [
  { key: "created_at", label: "When", sortable: true, width: 140 },
  { key: "user_email", label: "From", sortable: true, width: 220 },
  { key: "preview",    label: "Preview", sortable: false },
  { key: "status",     label: "Status",  sortable: false, width: 110 },
];

export default function Ideas({ socket }: IdeasPageProps) {
  const dispatch = useAppDispatch();
  const query = useAppSelector((s) => s.ideas.query);
  const rows = useAppSelector((s) => s.ideas.currentPage);
  const total = useAppSelector((s) => s.ideas.total);
  const loading = useAppSelector((s) => s.ideas.loading);
  const error = useAppSelector((s) => s.ideas.error);
  const page = useAppSelector((s) => s.ideas.page);
  const pageSize = useAppSelector((s) => s.ideas.pageSize);

  // Filter INPUT is local; the debounced value reaches Redux as query.q.
  const [filterInput, setFilterInput] = useState(query.q);
  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = filterInput.trim();
      if (trimmed !== query.q) {
        dispatch(ideasActions.setQuery({ q: trimmed, page: 1 }));
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [filterInput, query.q, dispatch]);

  // Row expand state.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ── Query effect — dispatch admin:story_ideas_query on any query change ──
  useEffect(() => {
    const sock = socket.current;
    if (!sock) return;
    let cancelled = false;
    const requestId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    dispatch(ideasActions.requestStarted({ requestId }));
    (async () => {
      try {
        const result = await new Promise<{
          requestId: string;
          ideas: AdminStoryIdea[];
          total: number;
          page: number;
          pageSize: number;
        }>((resolve, reject) => {
          if (!sock.connected) {
            reject(new RpcError("socket_not_connected"));
            return;
          }
          let settled = false;
          const timeout = window.setTimeout(() => {
            if (settled) return;
            settled = true;
            reject(new RpcError("timeout"));
          }, 15_000);
          sock.emit(
            "admin:story_ideas_query",
            {
              requestId,
              q: query.q || undefined,
              status: query.status,
              sort: query.sort,
              order: query.order,
              page: query.page,
              pageSize: query.pageSize,
            },
            (ack: unknown) => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeout);
              if (
                ack &&
                typeof ack === "object" &&
                (ack as { ok: boolean }).ok
              ) {
                resolve(
                  ack as {
                    requestId: string;
                    ideas: AdminStoryIdea[];
                    total: number;
                    page: number;
                    pageSize: number;
                  },
                );
              } else {
                const e =
                  (ack as { error?: string })?.error ?? "unknown_error";
                reject(new RpcError(e));
              }
            },
          );
        });
        if (cancelled) return;
        dispatch(
          ideasActions.requestSucceeded({
            requestId: result.requestId,
            ideas: result.ideas,
            total: result.total,
            page: result.page,
            pageSize: result.pageSize,
          }),
        );
      } catch (err) {
        if (cancelled) return;
        dispatch(
          ideasActions.requestFailed({
            requestId,
            error: err instanceof Error ? err.message : String(err),
          }),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    socket,
    dispatch,
    query.q,
    query.status,
    query.sort,
    query.order,
    query.page,
    query.pageSize,
  ]);

  function clickHeader(col: Column) {
    if (!col.sortable) return;
    const next = col.key as AdminStoryIdeasSort;
    if (next === query.sort) {
      dispatch(
        ideasActions.setQuery({
          order: query.order === "asc" ? "desc" : "asc",
          page: 1,
        }),
      );
    } else {
      dispatch(ideasActions.setQuery({ sort: next, order: "desc", page: 1 }));
    }
  }

  const sortValue = `${query.sort}:${query.order}`;
  function onSortChange(value: string) {
    const opt = SORT_OPTIONS.find((o) => o.value === value);
    if (!opt) return;
    dispatch(ideasActions.setQuery({ sort: opt.sort, order: opt.order, page: 1 }));
  }

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize],
  );

  return (
    <div>
      <h2 style={styles.pageTitle}>Ideas</h2>
      <p style={styles.pageSub}>
        {new Intl.NumberFormat("en-US").format(total)} submissions. Triage
        workflow is coming soon — currently view-only.
      </p>

      <div style={styles.controlsRow}>
        <input
          type="text"
          placeholder="Filter by body or email…"
          value={filterInput}
          onChange={(e) => setFilterInput(e.target.value)}
          style={styles.searchInput}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label style={controlLabelStyle}>
            Status
            <select
              value={query.status}
              onChange={(e) =>
                dispatch(
                  ideasActions.setQuery({
                    status: e.target.value as "all" | AdminStoryIdeaStatus,
                    page: 1,
                  }),
                )
              }
              style={styles.select}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label style={controlLabelStyle}>
            Sort
            <select
              value={sortValue}
              onChange={(e) => onSortChange(e.target.value)}
              style={styles.select}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label style={controlLabelStyle}>
            Rows per page
            <select
              value={pageSize}
              onChange={(e) =>
                dispatch(
                  ideasActions.setQuery({
                    pageSize: Number(e.target.value) as
                      (typeof PAGE_SIZES)[number],
                    page: 1,
                  }),
                )
              }
              style={styles.select}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const isActive = col.sortable && query.sort === col.key;
                const thStyle: CSSProperties = {
                  ...styles.th,
                  ...(col.sortable ? styles.thSortable : {}),
                  ...(isActive ? styles.thActive : {}),
                  width: col.width,
                  textAlign: col.align ?? "left",
                };
                return (
                  <th
                    key={col.key}
                    style={thStyle}
                    onClick={() => clickHeader(col)}
                  >
                    {col.label}
                    {isActive ? (query.order === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} style={styles.empty}>
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  style={{ ...styles.empty, color: tokens.destructive }}
                >
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} style={styles.empty}>
                  No ideas match.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <Row
                  key={row.id}
                  idea={row}
                  expanded={expandedId === row.id}
                  onToggleExpand={() =>
                    setExpandedId(expandedId === row.id ? null : row.id)
                  }
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pager}>
        <div>
          Page {page} of {totalPages}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() =>
              dispatch(ideasActions.setQuery({ page: Math.max(1, page - 1) }))
            }
            disabled={page <= 1 || loading}
            style={{
              ...styles.pagerBtn,
              ...(page <= 1 || loading ? styles.pagerBtnDisabled : {}),
            }}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() =>
              dispatch(
                ideasActions.setQuery({
                  page: Math.min(totalPages, page + 1),
                }),
              )
            }
            disabled={page >= totalPages || loading}
            style={{
              ...styles.pagerBtn,
              ...(page >= totalPages || loading
                ? styles.pagerBtnDisabled
                : {}),
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Row ────────────────────────────────────────────────────────────────

interface RowProps {
  idea: AdminStoryIdea;
  expanded: boolean;
  onToggleExpand: () => void;
}

function Row({ idea, expanded, onToggleExpand }: RowProps) {
  // Short preview for the collapsed row — first 160 chars-ish. The body
  // field may already be a 200-char preview if this row was prepended live;
  // either way truncate for display.
  const previewText = useMemo(() => {
    const single = idea.body.replace(/\s+/g, " ").trim();
    if (single.length <= 160) return single;
    return `${single.slice(0, 160)}…`;
  }, [idea.body]);

  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={onToggleExpand}>
        <td style={{ ...styles.td, ...styles.tdMono, fontSize: 12 }}>
          {formatWhen(idea.createdAt)}
        </td>
        <td style={styles.td}>
          <div style={fromCellStyle}>
            <span style={fromEmailStyle}>{idea.userEmail}</span>
            {idea.userName && (
              <span style={fromNameStyle}>{idea.userName}</span>
            )}
          </div>
        </td>
        <td style={styles.td}>
          <div style={previewCellStyle}>
            <span>{previewText}</span>
            <span style={expandToggleStyle} aria-hidden="true">
              {expanded ? "▾" : "▸"}
            </span>
          </div>
        </td>
        <td style={styles.td}>
          <StatusPill status={idea.status} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={COLUMNS.length} style={expandedCellStyle}>
            <div style={expandedShellStyle}>
              <div style={bodyHeaderStyle}>Body</div>
              <div style={bodyTextStyle}>{idea.body}</div>
              <div style={notesHeaderStyle}>
                Admin notes
                <span style={notesPlaceholderStyle}>
                  · Triage workflow coming soon — currently view-only
                </span>
              </div>
              <textarea
                value={idea.adminNotes ?? ""}
                readOnly
                disabled
                rows={3}
                style={notesTextareaStyle}
                placeholder="No notes yet."
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  disabled
                  style={{ ...styles.pagerBtn, ...styles.pagerBtnDisabled }}
                  title="Triage workflow coming soon"
                >
                  Save
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "—";
    const date = d.toISOString().slice(0, 10);
    const time = d.toISOString().slice(11, 16);
    return `${date} ${time}`;
  } catch {
    return iso;
  }
}

function StatusPill({ status }: { status: AdminStoryIdeaStatus }) {
  // Admin palette pills — coral for new (backlog), blue for triaged,
  // accent/green for shipped, muted for dismissed.
  let style: CSSProperties = styles.pillGrey;
  if (status === "new") {
    style = {
      color: tokens.destructive,
      background: tokens.destructiveMuted,
      borderColor: tokens.destructive,
    };
  } else if (status === "triaged") {
    style = styles.pillTrial; // blue accent
  } else if (status === "shipped") {
    style = styles.pillActive; // ok/green
  } else if (status === "dismissed") {
    style = {
      color: tokens.inkDim,
      background: tokens.panelAlt,
      borderColor: tokens.border,
    };
  }
  return <span style={{ ...styles.pill, ...style }}>{status}</span>;
}

// ── Styles ─────────────────────────────────────────────────────────────

const controlLabelStyle: CSSProperties = {
  color: tokens.inkMuted,
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const fromCellStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  lineHeight: 1.3,
};

const fromEmailStyle: CSSProperties = {
  color: tokens.inkPrimary,
  fontSize: 13,
};

const fromNameStyle: CSSProperties = {
  color: tokens.inkMuted,
  fontSize: 11,
};

const previewCellStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  color: tokens.inkPrimary,
};

const expandToggleStyle: CSSProperties = {
  color: tokens.inkDim,
  fontFamily: tokens.fontMono,
  fontSize: 12,
};

const expandedCellStyle: CSSProperties = {
  padding: 0,
  background: tokens.panelAlt,
  borderBottom: `1px solid ${tokens.border}`,
};

const expandedShellStyle: CSSProperties = {
  padding: "16px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const bodyHeaderStyle: CSSProperties = {
  fontSize: 11,
  color: tokens.inkMuted,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
};

const bodyTextStyle: CSSProperties = {
  color: tokens.inkPrimary,
  fontSize: 13,
  lineHeight: 1.55,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  padding: "8px 10px",
  background: tokens.panel,
  border: `1px solid ${tokens.border}`,
  borderRadius: 4,
};

const notesHeaderStyle: CSSProperties = {
  fontSize: 11,
  color: tokens.inkMuted,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 4,
};

const notesPlaceholderStyle: CSSProperties = {
  textTransform: "none",
  letterSpacing: "normal",
  color: tokens.inkDim,
  fontStyle: "italic",
};

const notesTextareaStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: tokens.panel,
  color: tokens.inkDim,
  border: `1px solid ${tokens.border}`,
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 13,
  lineHeight: 1.5,
  fontFamily: tokens.fontBody,
  resize: "vertical",
};
